import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export interface TeamSpace {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  visibility: 'PUBLIC' | 'PRIVATE';
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  isMember: boolean;
  // CHG-132: ARCHIVED_VIEWER 추가
  role: 'OWNER' | 'MANAGER' | 'MEMBER' | 'ARCHIVED_VIEWER' | null;
  creator: { id: string; name: string; avatarUrl: string | null };
  _count: { members: number };
  // 카드 노출용 문서 카운트 — PUBLISHED/LIVE는 전원, DRAFT는 본인이 만든 것만
  publishedCount: number;
  liveCount: number;
  myDraftCount: number;
  // CHG-132: PROJECT 스페이스의 경우 보관 메타
  type?: 'TEAM' | 'PROJECT';
  project?: { id: string; name: string; projectKey: string; archivedAt: string | null } | null;
}

export interface TeamSpaceDetail extends TeamSpace {
  members: TeamSpaceMember[];
}

export interface TeamSpaceMember {
  id: string;
  spaceId: string;
  userId: string;
  role: 'OWNER' | 'MANAGER' | 'MEMBER';
  joinedAt: string;
  // CHG-133 Step 7
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
}

export type TeamSpaceArchivedFilter = 'active' | 'archived' | 'all';

export async function fetchTeamSpaces(archived: TeamSpaceArchivedFilter = 'active') {
  const res = await api.get<ApiResponse<TeamSpace[]>>(`/team-spaces?archived=${archived}`);
  return res.data.data!;
}

export async function createTeamSpace(data: {
  name: string;
  description?: string;
  icon?: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  memberIds?: string[]; // CHG-130 REQ-DHR-019
}) {
  const res = await api.post<ApiResponse<TeamSpace>>('/team-spaces', data);
  return res.data.data!;
}

// CHG-130 REQ-DHR-018: 활성+이메일 인증 멤버 조회 (팀 문서함 초대용)
export interface ActiveMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  // CHG-133 Step 7
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export async function fetchActiveMembers(q: string): Promise<ActiveMember[]> {
  const res = await api.get<ApiResponse<ActiveMember[]>>(
    `/team-spaces/active-members?q=${encodeURIComponent(q)}`,
  );
  return res.data.data!;
}

export async function fetchTeamSpace(spaceId: string) {
  const res = await api.get<ApiResponse<TeamSpaceDetail>>(`/team-spaces/${spaceId}`);
  return res.data.data!;
}

export async function updateTeamSpace(
  spaceId: string,
  data: Partial<{ name: string; description: string | null; icon: string | null; visibility: 'PUBLIC' | 'PRIVATE' }>,
) {
  const res = await api.put<ApiResponse<TeamSpace>>(`/team-spaces/${spaceId}`, data);
  return res.data.data!;
}

export async function deleteTeamSpace(spaceId: string) {
  await api.delete(`/team-spaces/${spaceId}`);
}

export async function addTeamSpaceMember(spaceId: string, data: { userId: string; role?: 'MANAGER' | 'MEMBER' }) {
  const res = await api.post<ApiResponse<TeamSpaceMember>>(`/team-spaces/${spaceId}/members`, data);
  return res.data.data!;
}

export async function updateTeamSpaceMemberRole(
  spaceId: string,
  memberId: string,
  data: { role: 'OWNER' | 'MANAGER' | 'MEMBER' },
) {
  const res = await api.put<ApiResponse<TeamSpaceMember>>(`/team-spaces/${spaceId}/members/${memberId}`, data);
  return res.data.data!;
}

export async function removeTeamSpaceMember(spaceId: string, memberId: string) {
  await api.delete(`/team-spaces/${spaceId}/members/${memberId}`);
}

// CHG-132 REQ-PA-011: 조직 문서 검색 (archived PROJECT 포함)
export interface DocumentSearchResult {
  documentId: string;
  title: string;
  spaceId: string;
  spaceName: string;
  projectId: string | null;
  projectName: string | null;
  projectArchived: boolean;
  canRead: boolean;
  snippet?: string;
  createdBy: { id: string; name: string; avatarUrl: string | null };
  updatedAt: string;
}

export interface ArchivedSpaceSearchResult {
  spaceId: string;
  spaceName: string;
  projectId: string;
  projectName: string;
  archivedAt: string;
  canRead: boolean;
  documentCount: number;
}

export interface DocumentSearchResponse {
  results: DocumentSearchResult[];
  archivedSpaces: ArchivedSpaceSearchResult[];
  totalArchived: number;
}

export async function searchDocuments(query: string, limit = 50): Promise<DocumentSearchResponse> {
  const res = await api.get<ApiResponse<DocumentSearchResponse>>(
    `/team-spaces/search?q=${encodeURIComponent(query)}&limit=${limit}`,
  );
  return res.data.data!;
}
